<script setup lang="ts">
import Editor from "@tinymce/tinymce-vue";
import { useStorage } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
  ActionButtonFormElement,
  ActionButtonProperties,
  ActionButtonType,
  AnswerReviewFormElement,
  BooleanDisplayFormat,
  BooleanFormElement,
  CascaderFormElement,
  ChangeStatusButtonFormElement,
  CheckboxesFormElement,
  CheckboxOptionsDisplayFormat,
  ComplianceItemFormElement,
  DateFormElement,
  DiscussionFormElement,
  DividerFormElement,
  EnvironmentIndicatorFormElement,
  EntityFormElement,
  FileUploadFormElement,
  FormElementBase,
  FormElementDefaultProperties,
  FormElementType,
  FormResponseStatus,
  FormTextFormElement,
  HeadingFormElement,
  LinkedAnswerProperties,
  ListDefinition,
  MailingAddressFormElement,
  MoneyFormElement,
  NumberFormElement,
  PrimaryContactSelectFormElement,
  RadioButtonsDisplayFormat,
  RadioButtonsFormElement,
  CarryForwardMode,
  RepeaterFormElement,
  RichTextFormElement,
  RichTextPluginOptions,
  RichTextToolbarElement,
  SaveToTableTargetingMode,
  SectionFormElement,
  SecurityGroupFormElement,
  SelectListFormElement,
  SimpleTextFormElement,
  SpecialQuestion,
  SysOrgRoleName,
  Thickness,
  UserSelectFormElement,
  ValidationType,
  WorkflowDefinition,
} from "~/api";
import { siblingVersionsOf } from "~/NewFormBuilder/composables/useFormVersions";
import { nonQuestionElements } from "~/NewFormBuilder/types/NonQuestionElements";
import {
  autocompleteOptions,
  OptionGroup,
} from "~/NewFormBuilder/utils/autocompleteOptions";
import { sensitivityOptionsDictionary } from "~/NewFormBuilder/utils/dataSensitivityOptions";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { splitByCaps } from "~/utils/miscUtils";
import FormListsSelector from "./FormListsSelector.vue";
import LabelledOptionsEditor from "./LabelledOptionsEditor.vue";
import RecursiveListsSelector from "./RecursiveListsSelector.vue";

interface LabelledOption {
  value: any;
  label: string;
}

interface DefaultOption {
  inputType: string;
  label: string;
  placeholder?: string;
  noLabel?: boolean;
}

interface BasicOption extends DefaultOption {
  inputType:
    | "string"
    | "list"
    | "boolean"
    | "rich-text"
    | "rich-text-extended"
    | "dateCode"
    | "sensitivity"
    | "special-question"
    | "labelled-option-list-with-children";
}

interface LabelledOptionList extends DefaultOption {
  inputType: "labelled-option-list" | "labelled-option-list-with-children";
  noLabel: boolean;
  listDefinitions: ListDefinition[];
}

interface NumericalOption extends DefaultOption {
  inputType: "number";
  placeholder?: string;
  min?: number;
  max?: number;
}
interface StaticListOption extends DefaultOption {
  inputType: "static-list" | "data-sensitivity-option";
  multiple?: boolean;
  availableOptions: string[];
  valueOnClear?: string;
}

interface StaticListWithLabelsOption extends DefaultOption {
  inputType: "enum-based-list";
  multiple?: boolean;
  availableOptions: { l: string; v: string }[];
  valueOnClear?: string;
  clearable?: boolean;
}

interface StaticListKeyedOption extends DefaultOption {
  inputType: "static-list-keyed";
  multiple?: boolean;
  availableOptions: Record<string, string>;
}

interface StaticGroupedListOption extends DefaultOption {
  inputType: "static-option-group";
  multiple?: boolean;
  availableOptions: OptionGroup;
  valueOnClear?: string;
}

interface ApiListOption extends DefaultOption {
  inputType: "api-list-option";
  multiple?: boolean;
  getAvailableOptions?: (searchQuery: string) => Promise<LabelledOption[]>;
  valueOnClear?: string;
}

interface PreFetchedValuesListOption extends DefaultOption {
  inputType: "pre-fetched-values-list-option";
  multiple?: boolean;
  valueOnClear?: string;
  options?: LabelledOption[];
}

interface ColourPickerOption extends DefaultOption {
  inputType: "colour-picker";
  multiple?: boolean;
  availableOptions: LabelledOption[];
}

type Option =
  | BasicOption
  | NumericalOption
  | StaticListOption
  | StaticListKeyedOption
  | StaticListWithLabelsOption
  | StaticGroupedListOption
  | LabelledOptionList
  | ApiListOption
  | PreFetchedValuesListOption
  | ColourPickerOption;

interface KeyedOption {
  key: keyof AllPossibleProperties;
  propertyOption: Option;
}

// NOTE: WorkflowAssociationFormElement and ActivityDateFormElement are intentionally
// excluded from this intersection. Their generated Properties types are
// `FormElementDefaultProperties & Record<string, any>` (because the C# classes have
// no extra properties), which widens `keyof AllPossibleProperties` to `string` and
// destroys compile-time key checking. Their base properties are already captured via
// FormElementBase["properties"]. If those types gain concrete fields in the future,
// add them back here.
type AllPossibleProperties = FormElementBase["properties"] &
  ActionButtonFormElement["properties"] &
  AnswerReviewFormElement["properties"] &
  BooleanFormElement["properties"] &
  CascaderFormElement["properties"] &
  ChangeStatusButtonFormElement["properties"] &
  CheckboxesFormElement["properties"] &
  ComplianceItemFormElement["properties"] &
  DateFormElement["properties"] &
  DiscussionFormElement["properties"] &
  DividerFormElement["properties"] &
  EnvironmentIndicatorFormElement["properties"] &
  EntityFormElement["properties"] &
  FileUploadFormElement["properties"] &
  FormTextFormElement["properties"] &
  MailingAddressFormElement["properties"] &
  MoneyFormElement["properties"] &
  NumberFormElement["properties"] &
  HeadingFormElement["properties"] &
  RadioButtonsFormElement["properties"] &
  RepeaterFormElement["properties"] &
  RichTextFormElement["properties"] &
  SectionFormElement["properties"] &
  SecurityGroupFormElement["properties"] &
  SelectListFormElement["properties"] &
  SimpleTextFormElement["properties"] &
  UserSelectFormElement["properties"] &
  PrimaryContactSelectFormElement["properties"];

const modelValue = defineModel<FormElementBase>({
  required: true,
});

// Computed helper for editing dateShortcuts as a string[] in the UI while
// storing LabelledOption[] in the element properties (Label=Value=label).
const dateShortcutsSelected = computed<string[]>({
  get() {
    const props = modelValue.value.properties as any;
    const ds = props?.dateShortcuts;
    if (!ds || !ds.length) return [];
    const first = ds[0];
    if (typeof first === "string") return ds as string[];
    // Map objects to their label/value (support camelCase and PascalCase)
    return (ds as any[]).map(
      (s) => s.label ?? s.Label ?? s.value ?? s.Value ?? String(s),
    );
  },
  set(val: string[]) {
    const props = modelValue.value.properties as any;
    props.dateShortcuts = (val || []).map((l) => ({ label: l, value: l }));
  },
});

const disableDateShortcutOption = (label: string) => {
  const props = modelValue.value.properties as any;
  const allow = props?.allowPastDates;
  // When allowPastDates is explicitly false, disable these past-date shortcuts
  if (allow !== true) {
    return ["Yesterday", "7 Days Ago", "30 Days Ago", "90 Days Ago"].includes(
      label,
    );
  }
  return false;
};

// props
const props = defineProps<{
  workflowDefinition?: WorkflowDefinition;
  entityDefinitionsList?: LabelledOption[];
  formDefinitionsList?: LabelledOption[];
  securityGroupsList?: LabelledOption[];
  listDefinitionsList?: LabelledOption[];
  eventCodesList?: LabelledOption[];
  readOnly?: boolean;
}>();

const myInfoStore = useMyInfoStore();

/**
 * Question codes used in PREVIOUS versions of this form's template, each tagged with which
 * versions used it. Reusing one of these codes makes the answer carry forward across versions.
 * Empty when there is no workflow context or no sibling versions.
 */
const priorVersionCodes = computed<{ value: string; versions: string }[]>(
  () => {
    const allForms = props.workflowDefinition?.formDefinitions;
    const thisFormGuid = modelValue.value?.formDefinitionGuid;
    if (!allForms || !thisFormGuid) return [];

    const thisForm = allForms.find((fd) => fd.guid === thisFormGuid);
    if (!thisForm) return [];

    const siblings = siblingVersionsOf(thisForm, allForms).filter(
      (fd) => fd.guid !== thisFormGuid,
    );
    if (siblings.length === 0) return [];

    // Collect each prior code and the names of the versions that used it.
    const codeToVersions = new Map<string, Set<string>>();
    for (const sibling of siblings) {
      for (const c of sibling.components ?? []) {
        if (c.isArchived) continue;
        // Only suggest real question codes — section/layout/action elements aren't answerable, so
        // reusing their code across versions wouldn't (and shouldn't) link an answer.
        if (nonQuestionElements.includes(c.type)) continue;
        if (!c.questionCode) continue;
        if (!codeToVersions.has(c.questionCode)) {
          codeToVersions.set(c.questionCode, new Set());
        }
        codeToVersions.get(c.questionCode)!.add(sibling.name);
      }
    }

    return [...codeToVersions.entries()]
      .map(([value, versions]) => ({
        value,
        versions: [...versions].join(", "),
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  },
);

function queryPriorVersionCodes(
  queryString: string,
  cb: (results: { value: string; versions: string }[]) => void,
) {
  const q = queryString?.toLowerCase() ?? "";
  cb(
    q
      ? priorVersionCodes.value.filter((c) => c.value.toLowerCase().includes(q))
      : priorVersionCodes.value,
  );
}

const listDefinitions = useStorage<ListDefinition[]>(
  "listDefinitions",
  [],
  sessionStorage,
  {
    serializer: {
      read: (value) => JSON.parse(value),
      write: (value) => JSON.stringify(value),
    },
  },
);

const elementPropertyOptionsKeyed = computed<KeyedOption[]>(() => {
  const showAtBottom: (keyof FormElementDefaultProperties)[] = [
    // "isOptional",
    // "showOptionality",
    // "title",
    "sensitivity",
    "indentLevel",
    "specialQuestion",
  ];

  const keysForQuestionElements: (keyof FormElementDefaultProperties)[] = [
    "isOptional",
    "showOptionality",
    "title",
    "sensitivity",
    "indentLevel",
    "specialQuestion",
    "isReadOnly",
    "isOptional",
    "showOptionality",
    "carryForward",
    "makeAvailableToOtherForms",
    "specialQuestion",
  ];

  // Exclude certain properties for elements
  const excludedPropertiesForTypes: Record<string, string[]> = {
    [FormElementType.FORM_TEXT]: ["comment", "showCommentBelow"],
  };

  // ?? go through all keys in the AllPossibleProperties type and find matching in modelValue

  const customKeys: KeyedOption[] = Object.keys(modelValue.value.properties)
    //@ts-expect-error: we know this could be a key of default properties
    .filter((key) => !showAtBottom.includes(key))
    .filter((key) => {
      const excludedProperties =
        excludedPropertiesForTypes[modelValue.value.type];
      return !excludedProperties || !excludedProperties.includes(key);
    })
    .map((k) => {
      const key = k as keyof AllPossibleProperties;
      const keyedOption: KeyedOption = {
        key,
        propertyOption: elementPropertiesDict[key],
      };
      return keyedOption;
    });

  const keysAtBottom: KeyedOption[] = Object.keys(modelValue.value.properties)
    //@ts-expect-error: we know this could be a key of default properties
    .filter((key) => showAtBottom.includes(key))
    .map((k) => {
      const key = k as keyof AllPossibleProperties;
      const keyedOption: KeyedOption = {
        key,
        propertyOption: elementPropertiesDict[key],
      };
      return keyedOption;
    });

  // only want to show certain properties for compliance items
  if (modelValue.value.type === FormElementType.COMPLIANCE_ITEM) {
    return keysAtBottom;
  }

  if (nonQuestionElements.includes(modelValue.value.type)) {
    return customKeys.filter(
      (k) =>
        !keysForQuestionElements.includes(
          k.key as keyof FormElementDefaultProperties,
        ),
    );
  }

  return customKeys.concat(keysAtBottom);
});

const getAllListDefinitions = async () => {
  return await apiClient.listDefinition
    .getAllListDefinitions(
      modelValue.value.formDefinitionGuid,
      props.workflowDefinition?.guid,
      myInfoStore.currentSysOrgGuid,
    )
    .then((res) => {
      return res;
    })
    .catch((error) => {
      showError(error);
      return [];
    });
};

const elementPropertiesDict: Record<keyof AllPossibleProperties, Option> = {
  hideHeader: {
    label: "Hide Label",
    inputType: "boolean",
  },
  isOptional: {
    label: "Is Optional",
    inputType: "boolean",
  },
  isReadOnly: {
    label: "Is Read Only",
    inputType: "boolean",
  },
  showOptionality: {
    label: "Show Optionality",
    inputType: "boolean",
  },
  isMultiline: {
    label: "Multiple Lines",
    inputType: "boolean",
  },
  noDeselect: {
    label: "Disable Deselect",
    inputType: "boolean",
  },
  maxLength: {
    label: "Max Character Count",
    inputType: "number",
  },
  maxWordCount: {
    label: "Max Word Count",
    inputType: "number",
  },
  placeholder: {
    label: "Placeholder",
    inputType: "string",
  },
  indentLevel: {
    label: "Indent Level",
    inputType: "number",
    placeholder: "0",
    min: 0,
    max: 5,
  },
  title: {
    label: "Tool Tip",
    inputType: "rich-text",
  },
  carryForward: {
    label: "Carry Forward",
    inputType: "boolean",
  },
  excludeFromMainPdf: {
    label: "Exclude from Main PDF",
    inputType: "boolean",
  },

  customPdfCodes: {
    label: "Custom PDF Codes",
    inputType: "pre-fetched-values-list-option",
    options: props.listDefinitionsList,
    multiple: true,
  },
  makeAvailableToOtherForms: {
    label: "Make Available to Other Forms",
    inputType: "boolean",
  },
  toolbarElements: {
    label: "Toolbar Elements",
    inputType: "static-list",
    multiple: true,
    // auto-generated from API
    availableOptions: Object.values(RichTextToolbarElement),
  },
  pluginOptions: {
    label: "Plugins",
    inputType: "static-list",
    multiple: true,
    // auto-generated from API
    availableOptions: Object.values(RichTextPluginOptions),
  },
  max: {
    label: "Maximum",
    inputType: "number",
  },
  min: {
    label: "Minimum",
    inputType: "number",
  },
  enableTime: {
    label: "Enable Time Selection",
    inputType: "boolean",
  },
  maxDateCode: {
    label: "Max Date Code",
    inputType: "dateCode",
  },
  minDateCode: {
    label: "Min Date Code",
    inputType: "dateCode",
  },
  startDateCode: {
    label: "Starting Date Code",
    inputType: "dateCode",
  },
  precision: {
    label: "Precision",
    inputType: "number",
  },
  checkboxOptions: {
    label: "Items",
    inputType: "labelled-option-list",
    noLabel: false,
    listDefinitions: listDefinitions.value,
  },
  checkboxOptionsDisplayFormat: {
    label: "Display Format",
    inputType: "static-list",
    multiple: false,
    availableOptions: Object.values(CheckboxOptionsDisplayFormat),
    valueOnClear: CheckboxOptionsDisplayFormat.DEFAULT,
  },
  noFilesMessage: {
    label: "No Files Message",
    inputType: "string",
  },
  maxFiles: {
    label: "Max Files",
    inputType: "number",
  },
  compressImagesForNormalPrinting: {
    label: "Compress Images\nfor Normal Printing",
    inputType: "boolean",
  },
  accept: {
    label: "Accepted File Types",
    inputType: "enum-based-list",
    clearable: false,
    availableOptions: [
      // matches server-side restrictions
      {
        v: "image/*,application/pdf",
        l: "Images and PDFs",
      },
    ],
  },
  currency: {
    label: "Currency",
    inputType: "string",
  },
  radioButtonOptions: {
    label: "Items",
    inputType: "labelled-option-list",
    noLabel: false,
    listDefinitions: listDefinitions.value,
  },
  selectListOptions: {
    label: "Items",
    inputType: "labelled-option-list",
    noLabel: false,
    listDefinitions: listDefinitions.value,
  },
  cascaderOptions: {
    label: "Items",
    inputType: "labelled-option-list-with-children",
    listDefinitions: listDefinitions.value,
  },
  noneText: {
    label: "None Text",
    inputType: "string",
  },
  showControls: {
    label: "Show Controls",
    inputType: "boolean",
  },
  enableCopy: {
    label: "Enable Copy",
    inputType: "boolean",
  },
  generic: {
    label: "Generic Address",
    inputType: "boolean",
  },
  displayFormat: {
    label: "Display Format",
    inputType: "static-list",
    multiple: false,
    availableOptions:
      modelValue.value.type === FormElementType.BOOLEAN
        ? Object.values(BooleanDisplayFormat)
        : Object.values(RadioButtonsDisplayFormat),
  },
  content: {
    label: "Content",
    inputType: "rich-text",
    noLabel: true,
  },
  autocomplete: {
    label: "Autocomplete?",
    inputType: "static-option-group",
    availableOptions: autocompleteOptions,
    // valueOnClear: "off",
  },
  validationType: {
    label: "Validation Type",
    inputType: "static-list-keyed",
    availableOptions: ValidationType,
  },
  specialQuestion: {
    label: "Special Question",
    inputType: "enum-based-list",
    availableOptions: Object.entries(SpecialQuestion).map(([_, l]) => ({
      v: l, // should use v but openAPI changes case
      l: splitByCaps(l),
    })),
  },

  saveToTableName: {
    label: "Save to Table",
    inputType: "pre-fetched-values-list-option",
    options: [], // Will be populated on mount
  },

  saveToColumnName: {
    label: "Save to Column",
    inputType: "pre-fetched-values-list-option",
    options: [], // Will be populated when table is selected
  },

  saveToTargetingMode: {
    label: "Row Targeting Mode",
    inputType: "enum-based-list",
    availableOptions: Object.entries(SaveToTableTargetingMode).map(
      ([_, l]) => ({
        v: l,
        l: splitByCaps(l),
      }),
    ),
  },

  saveAnsweringUserGuid: {
    label: "Save Answering User Guid",
    inputType: "boolean",
  },

  carryForwardMode: {
    label: "Cross-Version Carry Forward",
    inputType: "enum-based-list",
    clearable: true,
    availableOptions: Object.entries(CarryForwardMode).map(([_, l]) => ({
      v: l,
      l: splitByCaps(l),
    })),
  },

  getFromLinkType: {
    label: "Linked Entity Type",
    inputType: "string",
  },

  sensitivity: {
    label: "Sensitivity Setting",
    inputType: "data-sensitivity-option",
    availableOptions: Object.keys(sensitivityOptionsDictionary),
  },

  headingLevel: {
    label: "Heading Level",
    inputType: "number",
    min: 0,
    max: 3,
  },

  displayLine: {
    label: "Display Horizontal Line",
    inputType: "boolean",
  },

  hideWhenLive: {
    label: "Hide On Live",
    inputType: "boolean",
  },

  prefix: {
    label: "Prefix Text",
    inputType: "string",
  },

  seedEnvironmentIntoQuestionCode: {
    label: "Seed Environment Into (Question Code)",
    inputType: "string",
  },

  defaultAnswer: {
    label: "Default Answer",
    inputType: "string",
  },

  deleteWorkflowOnClick: {
    label: "Delete Workflow On Click",
    inputType: "boolean",
  },

  thickness: {
    label: "Thickness",
    inputType: "static-list-keyed",
    availableOptions: Thickness,
  },

  entityDefinitionGuid: {
    label: "Entity Type",
    inputType: "pre-fetched-values-list-option",
    options: props.entityDefinitionsList,
  },

  entityDefinitionGuids: {
    label: "Entity Types",
    inputType: "pre-fetched-values-list-option",
    options: props.entityDefinitionsList,
    multiple: true,
  },

  linkType: {
    label: "Link Type",
    inputType: "string",
    placeholder: "e.g. Contact, Organization",
  },

  roleName: {
    label: "User Role Filter",
    inputType: "enum-based-list",
    clearable: true,
    availableOptions: Object.entries(SysOrgRoleName).map(([_, l]) => ({
      v: l,
      l: l,
    })),
  },

  allowedSecurityGroups: {
    label: "Entity Groups",
    inputType: "pre-fetched-values-list-option",
    options: props.securityGroupsList,
    multiple: true,
  },

  addPageBreakBefore: {
    label: "Page Break Before",
    inputType: "boolean",
  },
  allowPastDates: {
    label: "Allow Past Dates",
    inputType: "boolean",
  },
  remindersEnabled: {
    label: "Reminders Enabled",
    inputType: "boolean",
  },
  notifyTarget: {
    label: "Notify",
    inputType: "enum-based-list",
    clearable: true,
    availableOptions: [
      { v: "AnsweringUser", l: "User who answers the question" },
      { v: "FormCreator", l: "User who created the form" },
      { v: "UserSelectElement", l: "User from a User Select element" },
    ],
  },
  notifyUserSelectElementGuid: {
    label: "User Select Element",
    inputType: "pre-fetched-values-list-option",
    options: [], // populated reactively from the form's UserSelect elements
  },
  dateShortcuts: {
    label: "Date Shortcuts",
    inputType: "static-list",
    noLabel: false,
    multiple: true,
    availableOptions: [
      "Today",
      "Yesterday",
      "7 Days Ago",
      "30 Days Ago",
      "90 Days Ago",
      "Tomorrow",
      "In 1 Week",
      "In 30 Days",
      "In 90 Days",
    ],
  },
  nameStampButtonText: {
    label: "Approve Button Text",
    inputType: "string",
    placeholder: "Approve",
  },
  nameStampRevokeButtonText: {
    label: "Revoke Button Text",
    inputType: "string",
    placeholder: "Cancel",
  },
  isRevokable: {
    label: "Revokable",
    inputType: "boolean",
  },
  allowMultipleClicks: {
    label: "Allow Multiple Clicks",
    inputType: "boolean",
  },
  linkedFormDefinitionGuid: {
    label: "Linked Form Guid",
    inputType: "pre-fetched-values-list-option",
    options: props.formDefinitionsList,
  },
  linkedFormElementGuid: {
    label: "Linked Form Element",
    inputType: "api-list-option",
    getAvailableOptions: async () => {
      const linkedProperties = modelValue.value
        .properties as LinkedAnswerProperties;
      if (!linkedProperties.linkedFormDefinitionGuid) {
        return [];
      }
      const allElements = await apiClient.formElement.getAllFormElements(
        linkedProperties.linkedFormDefinitionGuid,
      );
      const result = allElements.map((el) => ({
        label: el.label ?? "",
        value: el.guid,
      }));
      apiListOptions.value = result;
      return result;
    },
  },
  questionsRemainingText: {
    label: "Form Incomplete Text",
    inputType: "rich-text",
  },
  noQuestionsRemainingText: {
    label: "Waiting for Confirmation Text",
    inputType: "rich-text",
  },
  statusUpdatedText: {
    label: "Form Complete Text",
    inputType: "rich-text",
  },
  reviewButtonText: {
    label: "Review Button Text",
    inputType: "string",
  },
  confirmButtonText: {
    label: "Confirm Button Text",
    inputType: "string",
  },
  hideConfirmButton: {
    label: "Hide Confirm Button",
    inputType: "boolean",
  },
  comment: {
    label: "Comment",
    inputType: "rich-text-extended",
  },
  showCommentBelow: {
    label: "Show Comment Below",
    inputType: "boolean",
  },
  leftAligned: {
    label: "Align to Left",
    inputType: "boolean",
  },
  saveAsDisplayed: {
    label: "Save As Displayed",
    inputType: "boolean",
  },
  isHidden: {
    label: "Is Hidden",
    inputType: "boolean",
  },
  buttonText: {
    label: "Button Text",
    inputType: "string",
  },
  actionToTake: {
    label: "Action to Take",
    inputType: "static-list-keyed",
    availableOptions: ActionButtonType,
  },

  code: {
    label: "Code",
    inputType: "api-list-option",
    getAvailableOptions: async (_searchQuery: string) => {
      const actionButtonProperties = modelValue.value
        .properties as ActionButtonProperties;

      if (actionButtonProperties.actionToTake === ActionButtonType.GET_PDF) {
        return props.listDefinitionsList ?? [];
      } else if (
        actionButtonProperties.actionToTake === ActionButtonType.SEND_EMAIL
      ) {
        return props.eventCodesList ?? [];
      }

      return [];
    },
  },

  actionButtonText: {
    label: "Action Button Text",
    inputType: "string",
    placeholder: "Action",
  },
  // showWhenStatus: {
  //   label: "Show When Status is",
  //   inputType: "static-list-keyed",
  //   availableOptions: FormResponseStatus,
  // },
  // hideWhenStatus: {
  //   label: "Hide When Status is",
  //   inputType: "static-list-keyed",
  //   availableOptions: FormResponseStatus,
  // },
  changeToStatus: {
    label: "Change Form Status To",
    inputType: "static-list-keyed",
    availableOptions: FormResponseStatus,
  },
  changeBackToStatus: {
    label: "Form Status if Revoked",
    inputType: "static-list-keyed",
    availableOptions: FormResponseStatus,
  },
  changeWorkflowStatusTo: {
    label: "Change Workflow Status To",
    inputType: "enum-based-list",
    availableOptions:
      props.workflowDefinition?.statusList
        ?.filter((s) => s.label !== null && s.code !== null)
        // using ! because we filtered nulls above
        .map((s) => ({ l: s.label!, v: s.code! })) ??
      // we should not fall back to default values here
      // because we need to show valid options,
      [],
  },
  changeWorkflowStatusBackTo: {
    label: "Workflow Status if Revoked",
    inputType: "enum-based-list",
    availableOptions:
      props.workflowDefinition?.statusList
        ?.filter((s) => s.label !== null && s.code !== null)
        // using ! because we filtered nulls above
        .map((s) => ({ l: s.label!, v: s.code! })) ??
      // we should not fall back to default values here
      // because we need to show valid options,
      [],
  },
  disableIfUnansweredQuestions: {
    label: "Disable If Unanswered Questions",
    inputType: "boolean",
  },
  customActionCode: {
    label: "Custom Action Code",
    inputType: "string",
  },
  // TODO: this is just for testing
  itemDefinitionGuid: {
    label: "Item Definition Guid",

    inputType: "string",
  },
  itemTitle: {
    label: "Item Title",
    inputType: "string",
  },
  addButtonText: {
    label: "Add Button Text",
    inputType: "string",
  },
  removeButtonText: {
    label: "Remove Button Text",
    inputType: "string",
  },
  noItemsText: {
    label: "No Items Text",
    inputType: "string",
  },
  showNumber: {
    label: "Show Repeater Numbers",
    inputType: "boolean",
  },
  defaultNumberToAdd: {
    label: "Default Number to Add",
    inputType: "number",
  },
  saveButtonText: {
    label: "Save Button Text",
    inputType: "string",
  },
  cancelButtonText: {
    label: "Cancel Button Text",
    inputType: "string",
  },
  startCollapsed: {
    label: "Start Collapsed",
    inputType: "boolean",
  },
  maxHeight: {
    label: "Max Height",
    inputType: "string",
  },
  colors: {
    label: "Colors",
    inputType: "colour-picker",
    multiple: true,
    availableOptions: [
      {
        value: "#EF9A9A",
        label: "light red",
      },
      {
        value: "#A5D6A7",
        label: "light green",
      },
      {
        value: "#90CAF9",
        label: "light blue",
      },
      {
        value: "#FFD54F",
        label: "yellow",
      },
      {
        value: "#CE93D8",
        label: "lavender",
      },
      {
        value: "#FFB74D",
        label: "orange",
      },
      {
        value: "#4DD0E1",
        label: "teal",
      },
      {
        value: "#BA68C8",
        label: "purple",
      },

      {
        value: "#C5E1A5",
        label: "lime green",
      },
    ],
  },
};

const showLabel = (type: FormElementType) => {
  return type !== FormElementType.FORM_TEXT;
};

const tinyMCEeditorStyles = `
 @font-face {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url(https://fonts.gstatic.com/s/poppins/v22/pxiEyp8kv8JHgFVrFJA.ttf)
    format("truetype");
}

body {
  font-family: "Poppins", sans-serif;
}
`
  .replace("\n", " ")
  .replace("\r", " ");

const tinyMCEOptionsForPropertyEditor = computed(() => {
  return {
    promotion: false,
    license_key: "gpl",
    plugins: "lists image charmap table advlist link autoresize save",
    toolbar: `
    bold italic fontsize forecolor  |
    undo redo removeformat`,
    toolbar_mode: "wrap",
    menubar: false,
    statusbar: false,
    resize: true,
    content_style: tinyMCEeditorStyles,
    font_size_formats: "8pt 10pt 12pt 14pt 15pt 18pt 24pt 36pt",
  };
});

const tinyMCEOptionsForPropertyEditorExtended = computed(() => {
  console.log(tinyMCEeditorStyles);
  return {
    promotion: false,
    license_key: "gpl",
    plugins: "lists image charmap table advlist link autoresize save code",
    toolbar: `
    bold italic underline fontsize forecolor superscript subscript |
    styles hr blockquote | bullist numlist indent outdent alignleft aligncenter |
    link table image charmap |
    undo redo | removeformat | code`,
    toolbar_mode: "wrap",
    style_formats_merge: true,
    style_formats: [
      {
        title: "In PDF",
        name: "inpdf",
        inline: "span",
        classes: ["inPdf"],
      },
      {
        title: "Not in PDF",
        name: "notinpdf",
        inline: "span",
        classes: ["notInPdf"],
      },
      {
        title: "Link not clickable",
        name: "noClick",
        inline: "span",
        classes: ["noClick"],
      },
    ],
    menubar: false,
    statusbar: false,
    resize: true,
    content_style: tinyMCEeditorStyles,
    font_size_formats: "8pt 10pt 12pt 14pt 15pt 18pt 24pt 36pt",
  };
});

const withPropertyOptions = computed(() => {
  return elementPropertyOptionsKeyed.value
    .filter((e) => {
      if (!e.propertyOption) return false;

      const specialQuestion = (modelValue.value.properties as any)
        .specialQuestion;
      const isSaveToTable = specialQuestion === "SaveToTable";
      const isGetFromTable = specialQuestion === "GetFromTable";
      const hasTableSelected = !!(modelValue.value.properties as any)
        .saveToTableName;

      // Show saveToTableName when SpecialQuestion is SaveToTable or GetFromTable
      if (e.key === "saveToTableName" && !isSaveToTable && !isGetFromTable) {
        return false;
      }

      // Show saveToColumnName when SpecialQuestion is SaveToTable/GetFromTable and a table is selected
      if (
        e.key === "saveToColumnName" &&
        ((!isSaveToTable && !isGetFromTable) || !hasTableSelected)
      ) {
        return false;
      }

      // Show saveToTargetingMode when SpecialQuestion is SaveToTable/GetFromTable and a table is selected
      if (
        e.key === "saveToTargetingMode" &&
        ((!isSaveToTable && !isGetFromTable) || !hasTableSelected)
      ) {
        return false;
      }

      // Only show saveAnsweringUserGuid when SpecialQuestion is SaveToTable and a table is selected
      if (
        e.key === "saveAnsweringUserGuid" &&
        (!isSaveToTable || !hasTableSelected)
      ) {
        return false;
      }

      // Only show getFromLinkType when SpecialQuestion is GetFromTable and a table is selected
      if (
        e.key === "getFromLinkType" &&
        (!isGetFromTable || !hasTableSelected)
      ) {
        return false;
      }

      // Only show notifyTarget when reminders are enabled on ActivityDate elements
      const isActivityDate =
        modelValue.value.type === FormElementType.ACTIVITY_DATE;
      const remindersEnabled = (modelValue.value.properties as any)
        .remindersEnabled;

      if (e.key === "notifyTarget" && (!isActivityDate || !remindersEnabled)) {
        return false;
      }

      // Only show notifyUserSelectElementGuid when notifyTarget is UserSelectElement
      if (e.key === "notifyUserSelectElementGuid") {
        const notifyTarget = (modelValue.value.properties as any).notifyTarget;
        if (!isActivityDate || notifyTarget !== "UserSelectElement") {
          return false;
        }
      }

      return true;
    })
    .map((e) => {
      // Override options for saveToTableName and saveToColumnName with reactive values
      if (e.key === "saveToTableName") {
        return {
          ...e,
          propertyOption: {
            ...e.propertyOption,
            options: availableTablesOptions.value,
          },
        };
      }
      // When actionToTake is Custom, allow free-text input for the code field
      if (e.key === "code") {
        const actionButtonProperties = modelValue.value
          .properties as ActionButtonProperties;
        if (actionButtonProperties.actionToTake === ActionButtonType.CUSTOM) {
          return {
            ...e,
            propertyOption: {
              label: "Code",
              inputType: "string" as const,
            },
          };
        }
      }
      if (e.key === "saveToColumnName") {
        return {
          ...e,
          propertyOption: {
            ...e.propertyOption,
            options: availableColumnsOptions.value,
          },
        };
      }
      if (e.key === "notifyUserSelectElementGuid") {
        return {
          ...e,
          propertyOption: {
            ...e.propertyOption,
            options: userSelectElementOptions.value,
          },
        };
      }
      return e;
    });
});

/** Mutable computed ref. Key in the sensitivity dict of the current sensitivity of this element. */
const elementSensitivityKey = computed<
  keyof typeof sensitivityOptionsDictionary | undefined
>({
  get: () => {
    // get the key's value from the sensitivity of the element provided
    const index = Object.values(sensitivityOptionsDictionary).findIndex(
      (o) =>
        modelValue.value.properties.sensitivity?.level === o.level &&
        modelValue.value.properties.sensitivity?.category === o.category,
    );
    return Object.keys(sensitivityOptionsDictionary)[index];
  },
  set: (value) =>
    (modelValue.value.properties.sensitivity =
      sensitivityOptionsDictionary[value ?? -1]),
});

const apiListOptions = ref<LabelledOption[]>([]);

// Options for SaveToTable feature
const availableTablesOptions = ref<LabelledOption[]>([]);
const availableColumnsOptions = ref<LabelledOption[]>([]);

// UserSelect elements on this form — options for the ActivityDate reminder's "User Select Element".
const userSelectElementOptions = ref<LabelledOption[]>([]);

const loadUserSelectElements = async () => {
  try {
    const elements = await apiClient.formElement.getAllFormElements(
      modelValue.value.formDefinitionGuid,
    );
    userSelectElementOptions.value = elements
      .filter((el) => el.type === FormElementType.USER_SELECT && el.guid)
      .map((el) => ({
        label: el.label || el.questionCode || el.guid!,
        value: el.guid!,
      }));
  } catch (error) {
    showError(error);
    userSelectElementOptions.value = [];
  }
};

// Watch for changes to saveToTableName and fetch columns for the selected table
watch(
  () => (modelValue.value.properties as any).saveToTableName,
  async (newTableName) => {
    if (newTableName) {
      try {
        const columns =
          await apiClient.formDesignerExtras.getApiMwformsFormDesignerExtrasGetTableColumns(
            newTableName,
            myInfoStore.currentSysOrgGuid,
          );
        availableColumnsOptions.value = columns
          .filter((c) => c.value !== null)
          .map((c) => ({
            label: String(c.value ?? ""),
            value: String(c.key ?? ""),
          }));
      } catch (error) {
        showError(error);
        availableColumnsOptions.value = [];
      }
    } else {
      availableColumnsOptions.value = [];
      // Clear the column selection when table is cleared
      (modelValue.value.properties as any).saveToColumnName = null;
    }
  },
  { immediate: true },
);

// Watch for changes to actionToTake and update apiListOptions accordingly
watch(
  () => (modelValue.value.properties as ActionButtonProperties).actionToTake,
  async (newAction) => {
    if (newAction === ActionButtonType.GET_PDF) {
      apiListOptions.value = props.listDefinitionsList ?? [];
    } else if (newAction === ActionButtonType.SEND_EMAIL) {
      apiListOptions.value = props.eventCodesList ?? [];
    } else {
      apiListOptions.value = [];
    }
  },
  { immediate: true },
);

// the defineModel macro only watches the top level ref, not the properties of the object
// we use this watch as a way to trigger updates when any properties are modified
// so we can easily use `v-model="modelValue.label"` for example
watch(
  () => modelValue.value,
  (updated) => (modelValue.value = { ...updated }),
  { deep: true },
);

const extendedEditorVisible = ref(
  modelValue.value.type === FormElementType.FORM_TEXT,
); // always show for now
const editorVisible = ref(true); // new
const usePredefinedLists = ref<boolean>(true);

const showExtendedEditor = () => {
  editorVisible.value = false; // hide the Editor first
  extendedEditorVisible.value = true;
  nextTick(() => {
    editorVisible.value = true; // after DOM updates, show the new Editor
  });
};

onMounted(async () => {
  if (
    modelValue.value.type === FormElementType.CHECKBOXES ||
    modelValue.value.type === FormElementType.RADIO_BUTTONS ||
    modelValue.value.type === FormElementType.SELECT_LIST ||
    modelValue.value.type === FormElementType.CASCADER
  ) {
    if (!listDefinitions.value || listDefinitions.value.length === 0) {
      // fetch predefined lists for elements that might use them
      listDefinitions.value = await getAllListDefinitions();
    }

    // Default the toggle to "Custom" if the element already has options that
    // don't match any predefined list (i.e. the designer entered a custom list).
    const listOptionKeys = [
      "checkboxOptions",
      "radioButtonOptions",
      "selectListOptions",
    ];
    for (const optionKey of listOptionKeys) {
      const options = (modelValue.value.properties as any)[optionKey] as
        | LabelledOption[]
        | null
        | undefined;
      if (options && options.length > 0) {
        const matchesPredefined = listDefinitions.value.some((list) =>
          options.every((option) =>
            list.items.some(
              (item) =>
                item.value === option.value && item.label === option.label,
            ),
          ),
        );
        if (!matchesPredefined) {
          usePredefinedLists.value = false;
        }
        break;
      }
    }
  }

  // ActivityDate reminders can notify a user picked from a UserSelect element on this form.
  if (modelValue.value.type === FormElementType.ACTIVITY_DATE) {
    await loadUserSelectElements();
  }

  // Fetch available tables for SaveToTable feature
  try {
    const tables =
      await apiClient.formDesignerExtras.getApiMwformsFormDesignerExtrasGetAvailableTables(
        myInfoStore.currentSysOrgGuid,
      );
    availableTablesOptions.value = tables
      .filter((t) => t.value !== null)
      .map((t) => ({
        label: String(t.value ?? ""),
        value: String(t.value ?? ""),
      }));
  } catch (error) {
    showError(error);
  }
});

const activeNames = ref(["1"]);
// const handleChange = (val: CollapseModelValue) => {
//   console.log(val);
// };
</script>

<template id="form-simple-text-option">
  <el-form
    label-width="auto"
    :disabled="!!modelValue.isArchived"
    class="elementsForms"
  >
    <el-form-item label="Question Code">
      <el-autocomplete
        v-model="modelValue.questionCode"
        :fetch-suggestions="queryPriorVersionCodes"
        :disabled="props.readOnly"
        placeholder="Question Code"
        style="width: 100%"
        :trigger-on-focus="priorVersionCodes.length > 0"
      >
        <template #default="{ item }">
          <div class="prior-code-suggestion">
            <code>{{ item.value }}</code>
            <span class="prior-code-versions">{{ item.versions }}</span>
          </div>
        </template>
      </el-autocomplete>
      <span v-if="priorVersionCodes.length" class="prior-code-hint">
        Reuse a code from a previous version to share its answer across
        versions.
      </span>
    </el-form-item>
    <el-form-item
      v-if="showLabel(modelValue.type)"
      label="Display Label"
      :disabled="props.readOnly"
    >
      <el-input
        v-model="modelValue.label"
        placeholder="Enter Label"
        :disabled="props.readOnly"
      ></el-input>
    </el-form-item>

    <el-form-item
      v-for="{ key, propertyOption } in withPropertyOptions"
      :key="key"
      :label="propertyOption.label"
      :class="{
        'form-item-wide-no-label': propertyOption.noLabel,
      }"
      :disabled="props.readOnly"
    >
      <template v-if="propertyOption.inputType === 'rich-text'">
        <template v-if="editorVisible">
          <div class="wrapTinyMce">
            <Editor
              v-model="(modelValue.properties as any)[key]"
              :init="
                extendedEditorVisible
                  ? tinyMCEOptionsForPropertyEditorExtended
                  : tinyMCEOptionsForPropertyEditor
              "
              :disabled="props.readOnly"
            />
            <el-button
              v-if="!extendedEditorVisible"
              size="small"
              :disabled="props.readOnly"
              @click="showExtendedEditor"
            >
              More Tools
            </el-button>
          </div>
        </template>
      </template>

      <template v-if="propertyOption.inputType === 'rich-text-extended'">
        <div class="wrapTinyMce">
          <Editor
            v-model="(modelValue.properties as any)[key]"
            :init="tinyMCEOptionsForPropertyEditorExtended"
            :disabled="props.readOnly"
          />
        </div>
      </template>

      <el-checkbox
        v-if="propertyOption.inputType === 'boolean'"
        v-model="(modelValue.properties as any)[key]"
        :disabled="props.readOnly"
      />
      <el-input
        v-if="propertyOption.inputType === 'string'"
        v-model="(modelValue.properties as any)[key]"
        :placeholder="propertyOption.placeholder"
        :disabled="props.readOnly"
      />
      <el-input-number
        v-if="propertyOption.inputType === 'number'"
        v-model="(modelValue.properties as any)[key]"
        :min="propertyOption.min"
        :max="propertyOption.max"
        :placeholder="propertyOption.placeholder"
        :disabled="props.readOnly"
      />
      <el-input
        v-if="propertyOption.inputType === 'dateCode'"
        v-model="(modelValue.properties as any)[key]"
        title="today
any date (2024-01-01)
y-1  (start of year)
m+3:e  (end of month)
d-7"
        placeholder="y-1:e"
        :disabled="props.readOnly"
      />

      <div
        v-if="propertyOption.inputType === 'static-list'"
        class="date-shortcuts-wrapper"
      >
        <el-select
          v-if="key === 'dateShortcuts'"
          v-model="dateShortcutsSelected"
          class="date-shortcuts-select"
          :multiple="propertyOption.multiple"
          placeholder="Select"
          clearable
          collapse-tags
          :max-collapse-tags="4"
          :disabled="props.readOnly"
        >
          <el-option
            v-for="item in propertyOption.availableOptions"
            :key="item"
            :label="item"
            :value="item"
            :disabled="disableDateShortcutOption(item)"
          />
        </el-select>

        <el-select
          v-else
          v-model="(modelValue.properties as any)[key]"
          :multiple="propertyOption.multiple"
          placeholder="Select"
          clearable
          collapse-tags
          :max-collapse-tags="4"
          :disabled="props.readOnly"
        >
          <el-option
            v-for="item in propertyOption.availableOptions"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </div>

      <el-select
        v-if="propertyOption.inputType === 'static-list-keyed'"
        v-model="(modelValue.properties as any)[key]"
        :multiple="propertyOption.multiple"
        placeholder="Select"
        clearable
        collapse-tags
        :max-collapse-tags="4"
        :disabled="props.readOnly"
      >
        <el-option
          v-for="(value, k) in propertyOption.availableOptions"
          :key="k"
          :label="k"
          :value="value"
        />
      </el-select>

      <el-select
        v-if="propertyOption.inputType === 'enum-based-list'"
        v-model="(modelValue.properties as any)[key]"
        :multiple="propertyOption.multiple"
        placeholder="Select"
        :clearable="propertyOption.clearable ?? true"
        collapse-tags
        :max-collapse-tags="4"
        :disabled="props.readOnly"
      >
        <el-option
          v-for="kvp in propertyOption.availableOptions"
          :key="kvp.v"
          :label="kvp.l"
          :value="kvp.v"
        />
      </el-select>

      <el-select
        v-if="propertyOption.inputType === 'list'"
        v-model="(modelValue.properties as any)[key]"
        multiple
        filterable
        allow-create
        default-first-option
        no-data-text="Type to add new entry!"
        :disabled="props.readOnly"
      >
      </el-select>

      <div
        v-if="propertyOption.inputType === 'labelled-option-list'"
        class="list-selector"
      >
        <!-- For ApplicationStatusSelect the items are provided dynamically by the backend
             and should not be editable in the designer. Show an informational message
             instead of the predefined/custom toggle and items editor. -->
        <template
          v-if="modelValue.type === FormElementType.APPLICATION_STATUS_SELECT"
        >
          <el-alert
            title="Options are dynamic"
            type="info"
            description="This element uses workflow-derived application statuses. Items are read-only and managed by the housing workflow."
            show-icon
          />
        </template>

        <template v-else>
          <el-switch
            v-model="usePredefinedLists"
            active-text="Predefined"
            inactive-text="Custom"
            :disabled="props.readOnly"
          />
          <FormListsSelector
            v-if="usePredefinedLists"
            v-model="(modelValue.properties as any)[key]"
            :form-definition-guid="modelValue.formDefinitionGuid"
            :list-definitions="listDefinitions"
          />

          <LabelledOptionsEditor
            v-else
            v-model="(modelValue.properties as any)[key]"
            :read-only="props.readOnly"
          />
        </template>
      </div>

      <div
        v-if="propertyOption.inputType === 'labelled-option-list-with-children'"
        class="labelled-option-list-with-children"
      >
        <el-collapse v-model="activeNames">
          <el-collapse-item title="Cascaded Lists" name="1">
            <RecursiveListsSelector
              v-model="(modelValue.properties as any)[key]"
              :form-definition-guid="modelValue.formDefinitionGuid"
              :list-definitions="listDefinitions"
              :read-only="props.readOnly"
            />
          </el-collapse-item>
        </el-collapse>
      </div>

      <el-select
        v-if="propertyOption.inputType === 'static-option-group'"
        v-model="(modelValue.properties as any)[key]"
        placeholder="Select an option"
        clearable
        :value-on-clear="propertyOption.valueOnClear"
        :disabled="props.readOnly"
      >
        <el-option-group
          v-for="group in propertyOption.availableOptions"
          :key="group.label"
          :label="group.label"
        >
          <el-option
            v-for="option in group.options"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-option-group>
      </el-select>

      <el-select-v2
        v-if="propertyOption.inputType === 'pre-fetched-values-list-option'"
        v-model="(modelValue.properties as any)[key]"
        placeholder="Select an option"
        filterable
        remote
        :multiple="propertyOption.multiple ?? false"
        :options="propertyOption.options ?? []"
        clearable
        :value-on-clear="propertyOption.valueOnClear"
        :disabled="props.readOnly"
      >
        <template #default="{ item }">
          <div style="width: 100%; display: flex">
            <span>{{ item.label }}</span>
            <span
              style="
                margin-left: auto;
                color: var(--el-text-color-secondary);
                font-size: 0.8em;
              "
            >
              ({{ item.value }})
            </span>
          </div>
        </template>
      </el-select-v2>

      <el-select-v2
        v-if="propertyOption.inputType === 'api-list-option'"
        v-model="(modelValue.properties as any)[key]"
        placeholder="Select an option"
        filterable
        remote
        :options="apiListOptions"
        :remote-method="propertyOption.getAvailableOptions"
        clearable
        :value-on-clear="propertyOption.valueOnClear"
        :disabled="props.readOnly"
      >
        <template #default="{ item }">
          <div style="width: 100%; display: flex">
            <span>{{ item.label }}</span>
            <span
              style="
                margin-left: auto;
                color: var(--el-text-color-secondary);
                font-size: 0.8em;
              "
            >
              ({{ item.value }})
            </span>
          </div>
        </template>
      </el-select-v2>

      <el-select
        v-if="propertyOption.inputType === 'data-sensitivity-option'"
        v-model="elementSensitivityKey"
        placeholder="Select an option"
        clearable
        :value-on-clear="propertyOption.valueOnClear"
        :disabled="props.readOnly"
      >
        <el-option
          v-for="sensitivityKey in Object.keys(sensitivityOptionsDictionary)"
          :key="sensitivityKey"
          :label="sensitivityKey"
          :value="sensitivityKey"
        />
      </el-select>

      <el-select
        v-if="propertyOption.inputType === 'colour-picker'"
        v-model="(modelValue.properties as any)[key]"
        multiple
        placeholder="Select"
        style="width: 240px"
        :disabled="props.readOnly"
      >
        <el-option
          v-for="item in propertyOption.availableOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        >
          <div class="flex items-center">
            <el-tag
              :color="item.value"
              style="margin-right: 8px"
              size="small"
            />
            <span :style="{ color: item.value }">{{ item.label }}</span>
          </div>
        </el-option>
        <template #tag>
          <el-tag
            v-for="color in (modelValue.properties as any)[key]"
            :key="color"
            :color="color"
          />
        </template>
      </el-select>
    </el-form-item>
  </el-form>
</template>

<style lang="less">
.prior-code-suggestion {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  code {
    font-family: monospace;
  }
  .prior-code-versions {
    color: var(--el-text-color-secondary);
    font-size: 0.85em;
  }
}
.prior-code-hint {
  color: var(--el-text-color-secondary);
  font-size: 0.8em;
}
.elementsForms {
  .form-item-wide-no-label {
    flex-direction: column;

    .el-form-item__label-wrap {
      display: none;
    }
  }
  .el-form-item__label {
    align-items: center;
    box-sizing: border-box;
    color: var(--el-text-color-regular);
    display: inline-flex;
    flex: 0 0 auto;
    font-size: var(--el-form-label-font-size);
    height: auto;
    justify-content: flex-end;
    line-height: 1.2;
    padding: 0 12px 0 0;
    white-space: normal;
    max-width: 190px;
    text-align: right;
  }
  .el-form-item {
    margin-bottom: 3px;
  }

  .el-select__wrapper {
    font-size: var(--el-font-size-base);
  }
  .wrapTinyMce {
    display: flex;
    flex-wrap: wrap;
    gap: 3px 10px;
  }

  .date-shortcuts-wrapper {
    width: 100%;
  }

  .date-shortcuts-wrapper .el-select {
    width: 100%;
  }

  .list-selector {
    width: 100%;
  }
  .labelled-option-list-with-children {
    width: 100%;
  }
}
</style>
